import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
    Plane, AlertTriangle, Clock, CheckCircle, XCircle, 
    RefreshCw, Plus, FileWarning, Bell, Activity, Calendar,
    ChevronLeft, ChevronRight, Zap, Wrench
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { UnconfirmedCrewCard } from '../components/crew/UnconfirmedCrewCard';
import { getLocalToday, addDaysLocal, parseLocalDate } from '../utils/dateUtils';
import AOGPanel from '../components/cco/AOGPanel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const flightStatuses = {
    scheduled: { label: 'Programado', color: 'bg-slate-500', textColor: 'text-slate-500' },
    confirmed: { label: 'Confirmado', color: 'bg-blue-500', textColor: 'text-blue-500' },
    preparing: { label: 'Preparando', color: 'bg-cyan-500', textColor: 'text-cyan-500' },
    boarding: { label: 'Abordando', color: 'bg-amber-500', textColor: 'text-amber-500' },
    closed: { label: 'Cerrado', color: 'bg-orange-500', textColor: 'text-orange-500' },
    departed: { label: 'Despegado', color: 'bg-emerald-500', textColor: 'text-emerald-500' },
    en_route: { label: 'En Vuelo', color: 'bg-green-500', textColor: 'text-green-500' },
    arrived: { label: 'Llegado', color: 'bg-emerald-600', textColor: 'text-emerald-600' },
    delayed: { label: 'Demorado', color: 'bg-yellow-500', textColor: 'text-yellow-500' },
    cancelled: { label: 'Cancelado', color: 'bg-red-500', textColor: 'text-red-500' }
};

// Auditoria 7 - Item 16: transiciones validas de estado (mismo que backend VALID_TRANSITIONS)
const VALID_NEXT_STATUS = {
    scheduled: ["confirmed", "preparing", "boarding", "delayed", "cancelled"],
    confirmed: ["preparing", "boarding", "delayed", "cancelled"],
    preparing: ["boarding", "delayed", "cancelled"],
    boarding: ["closed", "departed", "delayed", "cancelled"],
    closed: ["departed", "delayed", "cancelled"],
    departed: ["en_route", "arrived", "delayed"],
    en_route: ["arrived", "delayed"],
    delayed: ["confirmed", "preparing", "boarding", "closed", "departed", "en_route", "arrived", "cancelled"],
    arrived: [],
    cancelled: [],
};


const iropsCauses = [
    { value: 'weather', label: 'Meteorología' },
    { value: 'technical', label: 'Técnico' },
    { value: 'crew', label: 'Tripulación' },
    { value: 'atc', label: 'ATC' },
    { value: 'security', label: 'Seguridad' },
    { value: 'commercial', label: 'Comercial' },
    { value: 'passenger', label: 'Pasajeros' },
    { value: 'airport', label: 'Aeropuerto' },
    { value: 'other', label: 'Otro' }
];

export default function CCODashboardPage() {
    const { airline, token } = useAuth();
    const headers = { Authorization: `Bearer ${token}` };
    const [dashboardData, setDashboardData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(false);
    const [selectedDate, setSelectedDate] = useState(getLocalToday());
    const [selectedFlight, setSelectedFlight] = useState(null);
    const [iropsDialogOpen, setIropsDialogOpen] = useState(false);
    const [activeTab, setActiveTab] = useState('timeline');
    // Stage 4 - AOG count (para el badge del tab)
    const [aogCount, setAogCount] = useState(0);
    const timelineRef = useRef(null);
    
    const [newIrops, setNewIrops] = useState({
        flight_id: '',
        irops_type: 'delay',
        cause_code: '',
        description: '',
        affected_passengers: 0
    });

    useEffect(() => {
        fetchDashboardData();
        // Auto-refresh every 30 seconds
        const interval = setInterval(fetchDashboardData, 30000);
        return () => clearInterval(interval);
    }, [selectedDate]);

    const fetchDashboardData = async () => {
        try {
            const response = await axios.get(`${API}/cco/dashboard?date=${selectedDate}`, { headers });
            // Cerrar gap: /cco/dashboard filtra IROPS a {open, in_progress}. Complementamos con GET /irops
            // sin filtro para incluir tambien resolved/closed y permitir acciones Cerrar/Reabrir.
            try {
                const allIrops = await axios.get(`${API}/irops`, { headers });
                    setLoadError(false);
                setDashboardData({ ...response.data, irops: allIrops.data || response.data.irops });
            } catch {
                setLoadError(false);
                setDashboardData(response.data);
            }
            // Stage 4 - contador de AOG realmente abiertos para el badge del tab.
            // Bug fix: el filtro anterior '!== closed' contaba resolved+open. Ahora solo
            // cuenta events con status 'open' (o sin status por legacy).
            try {
                const aogRes = await axios.get(`${API}/aog`, { headers });
                const open = (aogRes.data || []).filter(e => (e.status || 'open') === 'open');
                setAogCount(open.length);
            } catch { /* silent */ }
        } catch (error) {
            console.error('Error fetching CCO dashboard:', error);
            setLoadError(true);
            if (!(error.response?.status >= 500)) {
                toast.error('No se pudo cargar el Centro de Control. Reintenta en unos segundos.', { id: 'cco-load-error' });
            }
        } finally {
            setLoading(false);
        }
    };

    const handleStatusChange = async (flightId, newStatus) => {
        try {
            await axios.patch(`${API}/flights/${flightId}/status?status=${newStatus}`, null, { headers });
            toast.success('Estado actualizado');
            fetchDashboardData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Error al actualizar estado');
        }
    };

    const handleCreateIrops = async (e) => {
        e.preventDefault();
        try {
            await axios.post(`${API}/irops`, newIrops, { headers });
            toast.success('IROPS creado exitosamente');
            setIropsDialogOpen(false);
            fetchDashboardData();
            setNewIrops({
                flight_id: '', irops_type: 'delay', cause_code: '',
                description: '', affected_passengers: 0
            });
        } catch (error) {
            toast.error('Error al crear IROPS');
        }
    };

    // Cerrar gap E2E: acción para cambiar estado del IROPS (in_progress / resolved / closed)
    const [resolveDialog, setResolveDialog] = useState(null);
    const [resolveNotes, setResolveNotes] = useState('');
    const [resolveTargetStatus, setResolveTargetStatus] = useState('resolved');
    const [savingResolve, setSavingResolve] = useState(false);

    const changeIropsStatus = async (event, newStatus, notes = '') => {
        try {
            await axios.patch(`${API}/irops/${event.id}/status`, {
                status: newStatus,
                resolution_notes: notes,
            }, { headers });
            toast.success(newStatus === 'resolved' ? 'IROPS resuelto' : newStatus === 'closed' ? 'IROPS cerrado' : 'IROPS actualizado');
            fetchDashboardData();
            setResolveDialog(null);
            setResolveNotes('');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Error al actualizar IROPS');
        }
    };

    const openResolveDialog = (item, target = 'resolved') => {
        setResolveDialog(item);
        setResolveTargetStatus(target);
        setResolveNotes('');
    };

    const submitResolve = async () => {
        if (!resolveDialog) return;
        setSavingResolve(true);
        await changeIropsStatus(resolveDialog, resolveTargetStatus, resolveNotes);
        setSavingResolve(false);
    };

    const formatTime = (dateStr) => {
        if (!dateStr) return '--:--';
        return new Date(dateStr).toLocaleTimeString('es', { hour: '2-digit', minute: '2-digit' });
    };

    const getTimePosition = (dateStr) => {
        if (!dateStr) return 0;
        const date = new Date(dateStr);
        const hours = date.getHours() + date.getMinutes() / 60;
        return (hours / 24) * 100;
    };

    const getFlightWidth = (dep, arr) => {
        if (!dep || !arr) return 5;
        const depDate = new Date(dep);
        const arrDate = new Date(arr);
        const duration = (arrDate - depDate) / (1000 * 60 * 60); // hours
        // A3: recortar al final del día visible (los vuelos que llegan al día siguiente no cubren toda la línea)
        const startH = depDate.getHours() + depDate.getMinutes() / 60;
        return Math.max((Math.min(duration, 24 - startH) / 24) * 100, 3);
    };
    const flightTimelineFlags = (f) => {
        if (!f.scheduled_departure || !f.scheduled_arrival) return { nextDay: false, long: false };
        const d = new Date(f.scheduled_departure), a = new Date(f.scheduled_arrival);
        return { nextDay: a.toDateString() !== d.toDateString(), long: (a - d) / 3600000 > 12, hours: Math.round((a - d) / 360000) / 10 };
    };

    const changeDate = (days) => {
        setSelectedDate(addDaysLocal(selectedDate, days));
    };

    if (loading) {
        return (
            <div className="space-y-4 animate-fade-in" data-testid="cco-loading">
                <div className="h-8 w-64 bg-muted rounded skeleton-pulse" />
                <div className="grid grid-cols-4 gap-4">
                    {[...Array(4)].map((_, i) => (
                        <div key={i} className="h-24 bg-muted rounded-lg skeleton-pulse" />
                    ))}
                </div>
                <div className="h-96 bg-muted rounded-lg skeleton-pulse" />
            </div>
        );
    }

    if (loadError && !dashboardData) {
        return (
            <Card className="card-aviation" data-testid="cco-load-error">
                <CardContent className="p-12 text-center text-muted-foreground space-y-4">
                    <AlertTriangle className="w-12 h-12 mx-auto text-amber-500" />
                    <p>No se pudo cargar el Centro de Control. Verifica tu conexión e inténtalo de nuevo.</p>
                    <Button onClick={() => { setLoading(true); fetchDashboardData(); }} data-testid="cco-retry-btn">
                        <RefreshCw className="w-4 h-4 mr-2" />Reintentar
                    </Button>
                </CardContent>
            </Card>
        );
    }

    const stats = dashboardData?.statistics || {};
    const flights = dashboardData?.flights || [];
    const aircraft = dashboardData?.aircraft || [];
    const irops = dashboardData?.irops || [];
    const docAlerts = dashboardData?.document_alerts || [];
    const urgentMessages = dashboardData?.urgent_messages || [];

    // Group flights by aircraft for Gantt view
    const flightsByAircraft = {};
    aircraft.forEach(ac => {
        flightsByAircraft[ac.id] = {
            registration: ac.registration,
            type: ac.aircraft_type,
            status: ac.status,
            flights: flights.filter(f => f.aircraft_id === ac.id)
        };
    });

    return (
        <div className="space-y-6 animate-fade-in" data-testid="cco-dashboard">
            <UnconfirmedCrewCard alwaysShow />
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold tracking-tight uppercase font-['Barlow_Condensed'] flex items-center gap-2">
                        <Activity className="w-8 h-8 text-primary" />
                        Centro de Control de Operaciones
                    </h1>
                    <p className="text-muted-foreground">
                        {airline?.name} - Control en tiempo real
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    <div className="flex items-center gap-1 bg-card border border-border rounded-lg p-1">
                        <Button variant="ghost" size="icon" onClick={() => changeDate(-1)} data-testid="cco-prev-day">
                            <ChevronLeft className="w-4 h-4" />
                        </Button>
                        <input
                            type="date"
                            value={selectedDate}
                            onChange={(e) => setSelectedDate(e.target.value)}
                            className="px-2 py-1 bg-transparent text-sm font-mono border-0 outline-none cursor-pointer"
                            data-testid="cco-date-picker"
                        />
                        <Button variant="ghost" size="icon" onClick={() => changeDate(1)} data-testid="cco-next-day">
                            <ChevronRight className="w-4 h-4" />
                        </Button>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => setSelectedDate(getLocalToday())} className="text-xs" data-testid="cco-today-btn">
                        Hoy
                    </Button>
                    <Button variant="outline" onClick={fetchDashboardData} data-testid="refresh-btn">
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Actualizar
                    </Button>
                    <Dialog open={iropsDialogOpen} onOpenChange={setIropsDialogOpen}>
                        <DialogTrigger asChild>
                            <Button className="btn-primary" data-testid="new-irops-btn">
                                <Plus className="w-4 h-4 mr-2" />
                                Nueva Irregularidad
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-lg">
                            <DialogHeader>
                                <DialogTitle className="font-['Barlow_Condensed'] text-2xl uppercase">
                                    Registrar IROPS
                                </DialogTitle>
                            </DialogHeader>
                            <form onSubmit={handleCreateIrops} className="space-y-4">
                                <div className="space-y-2">
                                    <label className="text-sm font-medium">Vuelo</label>
                                    <Select
                                        value={newIrops.flight_id}
                                        onValueChange={(v) => setNewIrops({...newIrops, flight_id: v})}
                                    >
                                        <SelectTrigger data-testid="irops-flight-select">
                                            <SelectValue placeholder="Seleccionar vuelo" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {flights.filter(f => !['arrived', 'cancelled'].includes(f.status)).map(f => (
                                                <SelectItem key={f.id} value={f.id}>
                                                    {f.flight_number} - {f.origin}→{f.destination}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <label className="text-sm font-medium">Tipo</label>
                                        <Select
                                            value={newIrops.irops_type}
                                            onValueChange={(v) => setNewIrops({...newIrops, irops_type: v})}
                                        >
                                            <SelectTrigger>
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="delay">Demora</SelectItem>
                                                <SelectItem value="cancellation">Cancelación</SelectItem>
                                                <SelectItem value="diversion">Desvío</SelectItem>
                                                <SelectItem value="return">Regreso</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-sm font-medium">Causa</label>
                                        <Select
                                            value={newIrops.cause_code}
                                            onValueChange={(v) => setNewIrops({...newIrops, cause_code: v})}
                                        >
                                            <SelectTrigger>
                                                <SelectValue placeholder="Seleccionar" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {iropsCauses.map(c => (
                                                    <SelectItem key={c.value} value={c.value}>
                                                        {c.label}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <label className="text-sm font-medium">Pasajeros Afectados</label>
                                    <Input
                                        type="number"
                                        value={newIrops.affected_passengers}
                                        onChange={(e) => setNewIrops({...newIrops, affected_passengers: parseInt(e.target.value) || 0})}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-sm font-medium">Descripción</label>
                                    <Textarea
                                        placeholder="Detalle de la irregularidad..."
                                        value={newIrops.description}
                                        onChange={(e) => setNewIrops({...newIrops, description: e.target.value})}
                                        required
                                        rows={3}
                                    />
                                </div>
                                <Button type="submit" className="w-full btn-primary">
                                    Registrar IROPS
                                </Button>
                            </form>
                        </DialogContent>
                    </Dialog>
                </div>
            </div>

            {/* KPI Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                <Card className="card-aviation" data-testid="kpi-total">
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                                <Plane className="w-5 h-5 text-primary" />
                            </div>
                            <div>
                                <p className="font-mono text-2xl font-bold">{stats.total_flights_today || 0}</p>
                                <p className="text-xs text-muted-foreground uppercase">Vuelos Hoy</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card className="card-aviation" data-testid="kpi-in-progress">
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                                <Activity className="w-5 h-5 text-emerald-500" />
                            </div>
                            <div>
                                <p className="font-mono text-2xl font-bold">{stats.in_progress || 0}</p>
                                <p className="text-xs text-muted-foreground uppercase">En Curso</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card className="card-aviation" data-testid="kpi-completed">
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                                <CheckCircle className="w-5 h-5 text-emerald-500" />
                            </div>
                            <div>
                                <p className="font-mono text-2xl font-bold">{stats.completed || 0}</p>
                                <p className="text-xs text-muted-foreground uppercase">Completados</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card className="card-aviation" data-testid="kpi-delayed">
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                                <Clock className="w-5 h-5 text-amber-500" />
                            </div>
                            <div>
                                <p className="font-mono text-2xl font-bold">{stats.delayed || 0}</p>
                                <p className="text-xs text-muted-foreground uppercase">Demorados</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card className="card-aviation" data-testid="kpi-cancelled">
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center">
                                <XCircle className="w-5 h-5 text-red-500" />
                            </div>
                            <div>
                                <p className="font-mono text-2xl font-bold">{stats.cancelled || 0}</p>
                                <p className="text-xs text-muted-foreground uppercase">Cancelados</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card className="card-aviation" data-testid="kpi-irops">
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center">
                                <AlertTriangle className="w-5 h-5 text-red-500" />
                            </div>
                            <div>
                                <p className="font-mono text-2xl font-bold">{stats.active_irops || 0}</p>
                                <p className="text-xs text-muted-foreground uppercase">IROPS</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Main Content Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList>
                    <TabsTrigger value="timeline" data-testid="tab-timeline">Línea de Tiempo</TabsTrigger>
                    <TabsTrigger value="list" data-testid="tab-list">Lista de Vuelos</TabsTrigger>
                    <TabsTrigger value="irops" data-testid="tab-irops">
                        IROPS
                        {irops.length > 0 && (
                            <Badge className="ml-2 bg-red-500 text-white">{irops.length}</Badge>
                        )}
                    </TabsTrigger>
                    <TabsTrigger value="alerts" data-testid="tab-alerts">
                        Alertas
                        {docAlerts.length > 0 && (
                            <Badge className="ml-2 bg-amber-500 text-white">{docAlerts.length}</Badge>
                        )}
                    </TabsTrigger>
                    <TabsTrigger value="aog" data-testid="tab-aog">
                        <Wrench className="w-3.5 h-3.5 mr-1" />
                        AOG
                        {aogCount > 0 && (
                            <Badge className="ml-2 bg-red-500 text-white">{aogCount}</Badge>
                        )}
                    </TabsTrigger>
                </TabsList>

                {/* Timeline View */}
                <TabsContent value="timeline" className="mt-4">
                    <Card className="card-aviation">
                        <CardHeader className="pb-2">
                            <CardTitle className="font-['Barlow_Condensed'] uppercase flex items-center gap-2">
                                <Calendar className="w-5 h-5 text-primary" />
                                Línea de Tiempo Operacional
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="overflow-x-auto" ref={timelineRef}>
                                {/* Time header */}
                                <div className="flex border-b border-border mb-2 min-w-[1200px]">
                                    <div className="w-32 flex-shrink-0 px-2 py-1 font-semibold text-xs text-muted-foreground">
                                        Aeronave
                                    </div>
                                    <div className="flex-1 flex">
                                        {[...Array(24)].map((_, i) => (
                                            <div key={i} className="flex-1 text-center text-xs text-muted-foreground border-l border-border/30 py-1">
                                                {String(i).padStart(2, '0')}:00
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                
                                {/* Aircraft rows */}
                                <div className="space-y-1 min-w-[1200px]">
                                    {Object.entries(flightsByAircraft).map(([acId, data]) => (
                                        <div key={acId} className="flex items-center h-12 hover:bg-muted/30 rounded">
                                            <div className="w-32 flex-shrink-0 px-2">
                                                <div className="font-mono font-bold text-sm text-primary">
                                                    {data.registration}
                                                </div>
                                                <div className="text-xs text-muted-foreground">{data.type}</div>
                                            </div>
                                            <div className="flex-1 relative h-full">
                                                {/* Time grid lines */}
                                                <div className="absolute inset-0 flex pointer-events-none">
                                                    {[...Array(24)].map((_, i) => (
                                                        <div key={i} className="flex-1 border-l border-border/20" />
                                                    ))}
                                                </div>
                                                
                                                {/* Current time indicator */}
                                                <div 
                                                    className="absolute top-0 bottom-0 w-0.5 bg-red-500 z-10"
                                                    style={{ left: `${getTimePosition(new Date().toISOString())}%` }}
                                                />
                                                
                                                {/* Flight blocks */}
                                                {data.flights.map((flight) => {
                                                    const left = getTimePosition(flight.scheduled_departure);
                                                    const width = getFlightWidth(flight.scheduled_departure, flight.scheduled_arrival);
                                                    const statusConfig = flightStatuses[flight.status] || flightStatuses.scheduled;
                                                    const tf = flightTimelineFlags(flight);
                                                    
                                                    return (
                                                        <div
                                                            key={flight.id}
                                                            className={`absolute top-1 bottom-1 rounded cursor-pointer ${statusConfig.color} hover:opacity-90 transition-opacity flex items-center justify-center overflow-hidden ${tf.long ? 'ring-2 ring-amber-400' : ''} ${tf.nextDay ? 'rounded-r-none border-r-4 border-dashed border-white/70' : ''}`}
                                                            style={{ 
                                                                left: `${left}%`, 
                                                                width: `${width}%`,
                                                                minWidth: '60px'
                                                            }}
                                                            onClick={() => setSelectedFlight(flight)}
                                                            title={`${flight.flight_number}: ${flight.origin}→${flight.destination}${tf.nextDay ? ' · llega al día siguiente' : ''}${tf.long ? ` · duración inusual (${tf.hours} h)` : ''}`}
                                                            data-testid={`timeline-flight-${flight.flight_number}`}
                                                        >
                                                            <div className="text-white text-xs font-bold px-1 truncate">
                                                                {tf.long && <AlertTriangle className="inline w-3 h-3 mr-0.5 -mt-0.5" data-testid={`timeline-long-${flight.flight_number}`} />}
                                                                {flight.flight_number}{tf.nextDay && <span className="ml-1 text-[9px] opacity-90" data-testid={`timeline-nextday-${flight.flight_number}`}>+1</span>}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    ))}
                                    
                                    {Object.keys(flightsByAircraft).length === 0 && (
                                        <div className="text-center py-8 text-muted-foreground">
                                            No hay aeronaves registradas
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Legend */}
                            <div className="mt-4 pt-4 border-t border-border flex flex-wrap gap-4 text-xs">
                                {Object.entries(flightStatuses).map(([key, config]) => (
                                    <div key={key} className="flex items-center gap-1">
                                        <div className={`w-3 h-3 rounded ${config.color}`} />
                                        <span className="text-muted-foreground">{config.label}</span>
                                    </div>
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* List View */}
                <TabsContent value="list" className="mt-4">
                    <Card className="card-aviation">
                        <CardContent className="p-0">
                            <ScrollArea className="h-[500px]">
                                <table className="table-aviation">
                                    <thead className="sticky top-0 bg-card z-10">
                                        <tr>
                                            <th>Vuelo</th>
                                            <th>Ruta</th>
                                            <th>Aeronave</th>
                                            <th>PAX</th>
                                            <th>STD</th>
                                            <th>ATD</th>
                                            <th>STA</th>
                                            <th>ATA</th>
                                            <th>Estado</th>
                                            <th>Accion</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {flights.length === 0 ? (
                                            <tr>
                                                <td colSpan={10} className="text-center py-8 text-muted-foreground">
                                                    No hay vuelos programados para hoy
                                                </td>
                                            </tr>
                                        ) : (
                                            flights.map((flight) => {
                                                const ac = aircraft.find(a => a.id === flight.aircraft_id);
                                                const statusConfig = flightStatuses[flight.status] || flightStatuses.scheduled;
                                                
                                                return (
                                                    <tr key={flight.id} data-testid={`flight-row-${flight.flight_number}`}>
                                                        <td className="font-mono font-bold text-primary">
                                                            {flight.flight_number}
                                                            {flight.validation_errors?.length > 0 && (
                                                                <AlertTriangle className="w-3 h-3 text-red-500 inline ml-1" />
                                                            )}
                                                        </td>
                                                        <td>
                                                            <span className="font-mono">{flight.origin}</span>
                                                            <span className="mx-1 text-muted-foreground">→</span>
                                                            <span className="font-mono">{flight.destination}</span>
                                                        </td>
                                                        <td className="font-mono">{ac?.registration || '-'}</td>
                                                        <td>
                                                            {(() => {
                                                                const pax = flight.pax_total || flight.passengers || 0;
                                                                const reserved = flight.pax_reserved || 0;
                                                                const cap = flight.aircraft_capacity || 0;
                                                                const loadPct = cap > 0 ? Math.round((reserved / cap) * 100) : 0;
                                                                const color = cap === 0 ? 'text-muted-foreground' : loadPct >= 80 ? 'text-emerald-500' : loadPct >= 50 ? 'text-amber-500' : 'text-muted-foreground';
                                                                return (
                                                                    <span className={`font-mono text-xs ${color}`} data-testid={`cco-pax-${flight.id}`}>
                                                                        {reserved > 0 ? `${reserved}` : pax}{cap > 0 ? `/${cap}` : ''}
                                                                        {cap > 0 && <span className="ml-1">({loadPct}%)</span>}
                                                                    </span>
                                                                );
                                                            })()}
                                                        </td>
                                                        <td className="font-mono">{formatTime(flight.scheduled_departure)}</td>
                                                        <td className="font-mono">{formatTime(flight.actual_departure)}</td>
                                                        <td className="font-mono">{formatTime(flight.scheduled_arrival)}</td>
                                                        <td className="font-mono">{formatTime(flight.actual_arrival)}</td>
                                                        <td>
                                                            <Badge className={`${statusConfig.color} text-white`}>
                                                                {statusConfig.label}
                                                            </Badge>
                                                            {flight.delay_minutes > 0 && (
                                                                <span className="ml-1 text-xs text-amber-500">
                                                                    +{flight.delay_minutes}m
                                                                </span>
                                                            )}
                                                        </td>
                                                        <td>
                                                            {!['arrived', 'cancelled'].includes(flight.status) && (
                                                                <Select
                                                                    value={flight.status}
                                                                    onValueChange={(v) => handleStatusChange(flight.id, v)}
                                                                >
                                                                    <SelectTrigger className="w-28 h-7 text-xs" data-testid={`cco-status-select-${flight.id}`}>
                                                                        <SelectValue />
                                                                    </SelectTrigger>
                                                                    <SelectContent>
                                                                        {/* current sigue visible pero deshabilitado; solo permite avanzar a estados validos */}
                                                                        <SelectItem value={flight.status} disabled>
                                                                            {flightStatuses[flight.status]?.label || flight.status}
                                                                        </SelectItem>
                                                                        {(VALID_NEXT_STATUS[flight.status] || []).map(next => (
                                                                            <SelectItem key={next} value={next}>
                                                                                {flightStatuses[next]?.label || next}
                                                                            </SelectItem>
                                                                        ))}
                                                                    </SelectContent>
                                                                </Select>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        )}
                                    </tbody>
                                </table>
                            </ScrollArea>
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* IROPS View */}
                <TabsContent value="irops" className="mt-4">
                    <div className="space-y-3">
                        {irops.length === 0 ? (
                            <Card className="card-aviation">
                                <CardContent className="py-12 text-center text-muted-foreground">
                                    <CheckCircle className="w-12 h-12 mx-auto mb-4 opacity-50 text-emerald-500" />
                                    <p>No hay irregularidades activas</p>
                                </CardContent>
                            </Card>
                        ) : (
                            irops.map((item) => {
                                const flight = flights.find(f => f.id === item.flight_id);
                                const causeLabel = iropsCauses.find(c => c.value === item.cause_code)?.label || item.cause_code;
                                
                                return (
                                    <Card key={item.id} className="card-aviation border-l-4 border-l-red-500" data-testid={`irops-card-${item.id}`}>
                                        <CardContent className="p-4">
                                            <div className="flex items-start justify-between gap-4">
                                                <div className="flex items-start gap-4">
                                                    <div className="w-12 h-12 rounded-lg bg-red-500/10 flex items-center justify-center flex-shrink-0">
                                                        <AlertTriangle className="w-6 h-6 text-red-500" />
                                                    </div>
                                                    <div>
                                                        <div className="flex items-center gap-2 mb-1">
                                                            <span className="font-mono font-bold text-primary">
                                                                {flight?.flight_number || 'N/A'}
                                                            </span>
                                                            <Badge variant="outline" className="uppercase">
                                                                {item.irops_type}
                                                            </Badge>
                                                            <Badge variant="outline">
                                                                {causeLabel}
                                                            </Badge>
                                                            <Badge className={
                                                                item.status === 'resolved' ? 'status-success' :
                                                                item.status === 'closed' ? 'status-info' :
                                                                item.status === 'in_progress' ? 'status-warning' :
                                                                'status-danger'
                                                            }>
                                                                {item.status === 'open' ? 'Abierto' :
                                                                    item.status === 'in_progress' ? 'En Progreso' :
                                                                    item.status === 'resolved' ? 'Resuelto' :
                                                                    item.status === 'closed' ? 'Cerrado' : item.status}
                                                            </Badge>
                                                        </div>
                                                        <p className="text-sm">{item.description}</p>
                                                        <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                                                            <span>PAX afectados: {item.affected_passengers}</span>
                                                            <span>Abierto: {new Date(item.opened_at || item.created_at).toLocaleTimeString('es', { hour: '2-digit', minute: '2-digit' })}</span>
                                                            {item.resolved_at && (
                                                                <span className="text-emerald-500">Resuelto: {new Date(item.resolved_at).toLocaleString('es', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit' })}</span>
                                                            )}
                                                        </div>
                                                        {item.resolution_notes && (
                                                            <div className="mt-2 text-xs bg-emerald-500/5 border border-emerald-500/20 rounded p-2">
                                                                <p className="font-semibold text-emerald-500">Resolución:</p>
                                                                <p className="text-muted-foreground">{item.resolution_notes}</p>
                                                            </div>
                                                        )}
                                                        {item.actions_taken?.length > 0 && (
                                                            <div className="mt-2 text-xs">
                                                                <p className="font-semibold">Acciones:</p>
                                                                <ul className="list-disc list-inside text-muted-foreground">
                                                                    {item.actions_taken.slice(-3).map((action, i) => (
                                                                        <li key={i}>{action}</li>
                                                                    ))}
                                                                </ul>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                                {/* Cerrar gap E2E: botones de accion para cambiar estado del IROPS */}
                                                <div className="flex flex-col gap-1 flex-shrink-0">
                                                    {item.status === 'open' && (
                                                        <Button size="sm" variant="outline" className="h-7 text-[10px]" onClick={() => changeIropsStatus(item, 'in_progress')} data-testid={`irops-progress-${item.id}`}>
                                                            En Progreso
                                                        </Button>
                                                    )}
                                                    {(item.status === 'open' || item.status === 'in_progress') && (
                                                        <Button size="sm" variant="outline" className="h-7 text-[10px] text-emerald-500 border-emerald-500/40" onClick={() => openResolveDialog(item, 'resolved')} data-testid={`irops-resolve-${item.id}`}>
                                                            <CheckCircle className="w-3 h-3 mr-1" />Resolver
                                                        </Button>
                                                    )}
                                                    {item.status === 'resolved' && (
                                                        <Button size="sm" variant="ghost" className="h-7 text-[10px] text-muted-foreground" onClick={() => openResolveDialog(item, 'closed')} data-testid={`irops-close-${item.id}`}>
                                                            Cerrar
                                                        </Button>
                                                    )}
                                                    {(item.status === 'resolved' || item.status === 'closed') && (
                                                        <Button size="sm" variant="ghost" className="h-7 text-[10px] text-amber-500" onClick={() => changeIropsStatus(item, 'open')} data-testid={`irops-reopen-${item.id}`}>
                                                            Reabrir
                                                        </Button>
                                                    )}
                                                </div>
                                            </div>
                                        </CardContent>
                                    </Card>
                                );
                            })
                        )}
                    </div>
                </TabsContent>

                {/* Alerts View */}
                <TabsContent value="alerts" className="mt-4">
                    <div className="space-y-3">
                        {docAlerts.length === 0 ? (
                            <Card className="card-aviation">
                                <CardContent className="py-12 text-center text-muted-foreground">
                                    <CheckCircle className="w-12 h-12 mx-auto mb-4 opacity-50 text-emerald-500" />
                                    <p>No hay alertas de documentos</p>
                                </CardContent>
                            </Card>
                        ) : (
                            docAlerts.map((alert, index) => (
                                <Card 
                                    key={index} 
                                    className={`card-aviation border-l-4 ${
                                        alert.days_until_expiry <= 0 ? 'border-l-red-500' :
                                        alert.days_until_expiry <= 7 ? 'border-l-amber-500' : 'border-l-blue-500'
                                    }`}
                                >
                                    <CardContent className="p-4 flex items-center gap-4">
                                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                                            alert.days_until_expiry <= 0 ? 'bg-red-500/10' :
                                            alert.days_until_expiry <= 7 ? 'bg-amber-500/10' : 'bg-blue-500/10'
                                        }`}>
                                            <FileWarning className={`w-5 h-5 ${
                                                alert.days_until_expiry <= 0 ? 'text-red-500' :
                                                alert.days_until_expiry <= 7 ? 'text-amber-500' : 'text-blue-500'
                                            }`} />
                                        </div>
                                        <div className="flex-1">
                                            <p className="font-semibold">{alert.document_name}</p>
                                            <p className="text-sm text-muted-foreground">
                                                {alert.reference_type === 'aircraft' ? 'Aeronave' : 'Tripulante'}: {alert.reference_id}
                                            </p>
                                        </div>
                                        <div className="text-right">
                                            <Badge className={
                                                alert.days_until_expiry <= 0 ? 'status-danger' :
                                                alert.days_until_expiry <= 7 ? 'status-warning' : 'status-info'
                                            }>
                                                {alert.days_until_expiry <= 0 ? 'VENCIDO' : `${alert.days_until_expiry} días`}
                                            </Badge>
                                            <p className="text-xs text-muted-foreground mt-1">
                                                {new Date(alert.expiry_date).toLocaleDateString('es')}
                                            </p>
                                        </div>
                                    </CardContent>
                                </Card>
                            ))
                        )}
                    </div>
                </TabsContent>

                {/* Stage 4 - AOG Impact Tab */}
                <TabsContent value="aog" className="mt-4">
                    <AOGPanel token={token} />
                </TabsContent>
            </Tabs>

            {/* Flight Detail Modal */}
            {selectedFlight && (
                <Dialog open={!!selectedFlight} onOpenChange={() => setSelectedFlight(null)}>
                    <DialogContent className="max-w-lg">
                        <DialogHeader>
                            <DialogTitle className="font-['Barlow_Condensed'] text-2xl uppercase flex items-center gap-2">
                                <Plane className="w-6 h-6 text-primary" />
                                {selectedFlight.flight_number}
                            </DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4">
                            <div className="flex items-center justify-center gap-4 py-4">
                                <div className="text-center">
                                    <p className="font-mono text-3xl font-bold">{selectedFlight.origin}</p>
                                    <p className="text-xs text-muted-foreground">
                                        STD: {formatTime(selectedFlight.scheduled_departure)}
                                    </p>
                                    {selectedFlight.actual_departure && (
                                        <p className="text-xs text-emerald-500">
                                            ATD: {formatTime(selectedFlight.actual_departure)}
                                        </p>
                                    )}
                                </div>
                                <div className="flex-1 max-w-[100px] border-t-2 border-dashed border-border relative">
                                    <Plane className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-5 h-5 text-primary rotate-90" />
                                </div>
                                <div className="text-center">
                                    <p className="font-mono text-3xl font-bold">{selectedFlight.destination}</p>
                                    <p className="text-xs text-muted-foreground">
                                        STA: {formatTime(selectedFlight.scheduled_arrival)}
                                    </p>
                                    {selectedFlight.actual_arrival && (
                                        <p className="text-xs text-emerald-500">
                                            ATA: {formatTime(selectedFlight.actual_arrival)}
                                        </p>
                                    )}
                                </div>
                            </div>
                            
                            <div className="grid grid-cols-2 gap-4 text-sm">
                                <div>
                                    <p className="text-muted-foreground">Estado</p>
                                    <Badge className={`${flightStatuses[selectedFlight.status]?.color} text-white mt-1`}>
                                        {flightStatuses[selectedFlight.status]?.label}
                                    </Badge>
                                </div>
                                <div>
                                    <p className="text-muted-foreground">Pasajeros</p>
                                    {(() => {
                                        const pax = selectedFlight.pax_total || selectedFlight.passengers || 0;
                                        const reserved = selectedFlight.pax_reserved || 0;
                                        const cap = selectedFlight.aircraft_capacity || 0;
                                        const loadPct = cap > 0 ? Math.round((reserved / cap) * 100) : 0;
                                        return (
                                            <div>
                                                {reserved > 0 && <p className="text-xs text-muted-foreground">Reservados: <span className="font-mono font-bold">{reserved}</span></p>}
                                                <p className="font-mono font-bold">{pax}{cap > 0 ? `/${cap}` : ''} PAX</p>
                                                {cap > 0 && (
                                                    <div className="mt-1">
                                                        <div className="w-full h-1.5 rounded-full bg-muted overflow-hidden">
                                                            <div className={`h-full rounded-full transition-all ${loadPct >= 80 ? 'bg-emerald-500' : loadPct >= 50 ? 'bg-amber-500' : 'bg-blue-400'}`} style={{ width: `${Math.min(loadPct, 100)}%` }} />
                                                        </div>
                                                        <p className="text-xs text-muted-foreground mt-0.5">{loadPct}% ocupacion est.</p>
                                                    </div>
                                                )}
                                                {(selectedFlight.pax_checked_in > 0 || selectedFlight.pax_boarded > 0) && (
                                                    <p className="text-xs text-muted-foreground mt-0.5">
                                                        Check-in: {selectedFlight.pax_checked_in || 0} | Abordados: {selectedFlight.pax_boarded || 0}
                                                    </p>
                                                )}
                                            </div>
                                        );
                                    })()}
                                </div>
                            </div>
                            
                            {selectedFlight.validation_errors?.length > 0 && (
                                <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                                    <p className="text-sm font-semibold text-red-500 mb-2">Errores de Validación:</p>
                                    <ul className="text-xs text-red-400 space-y-1">
                                        {selectedFlight.validation_errors.map((err, i) => (
                                            <li key={i}>• {err}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                            
                            {selectedFlight.validation_warnings?.length > 0 && (
                                <div className="p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg">
                                    <p className="text-sm font-semibold text-amber-500 mb-2">Advertencias:</p>
                                    <ul className="text-xs text-amber-400 space-y-1">
                                        {selectedFlight.validation_warnings.map((warn, i) => (
                                            <li key={i}>• {warn}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                            
                            {!['arrived', 'cancelled'].includes(selectedFlight.status) && (
                                <div className="flex gap-2">
                                    <Select
                                        value={selectedFlight.status}
                                        onValueChange={(v) => {
                                            handleStatusChange(selectedFlight.id, v);
                                            setSelectedFlight(null);
                                        }}
                                    >
                                        <SelectTrigger className="flex-1">
                                            <SelectValue placeholder="Cambiar estado" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {Object.entries(flightStatuses).map(([key, config]) => (
                                                <SelectItem key={key} value={key}>
                                                    {config.label}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <Button
                                        variant="destructive"
                                        onClick={() => {
                                            setNewIrops({ ...newIrops, flight_id: selectedFlight.id });
                                            setSelectedFlight(null);
                                            setIropsDialogOpen(true);
                                        }}
                                    >
                                        <AlertTriangle className="w-4 h-4 mr-1" />
                                        IROPS
                                    </Button>
                                </div>
                            )}
                        </div>
                    </DialogContent>
                </Dialog>
            )}

            {/* Cerrar gap E2E: dialogo para resolver/cerrar IROPS con notas */}
            <Dialog open={!!resolveDialog} onOpenChange={(o) => { if (!o) { setResolveDialog(null); setResolveNotes(''); } }}>
                <DialogContent className="sm:max-w-[500px]" data-testid="irops-resolve-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-['Barlow_Condensed'] text-xl uppercase flex items-center gap-2">
                            <CheckCircle className="w-5 h-5 text-emerald-500" />
                            {resolveTargetStatus === 'closed' ? 'Cerrar IROPS' : 'Resolver IROPS'}
                        </DialogTitle>
                        <DialogDescription>
                            {resolveTargetStatus === 'closed'
                                ? 'Cierre definitivo del evento. Solo se cierra un IROPS ya resuelto.'
                                : 'Registra la solución aplicada. Queda en el histórico y en el log de auditoría.'}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                        {resolveDialog && (
                            <div className="p-3 rounded-lg bg-muted/40 border border-border/50 text-xs">
                                <p className="font-semibold uppercase text-muted-foreground mb-1">Evento</p>
                                <p>{resolveDialog.description}</p>
                                <p className="mt-1 text-muted-foreground">Causa: {iropsCauses.find(c => c.value === resolveDialog.cause_code)?.label || resolveDialog.cause_code}</p>
                            </div>
                        )}
                        <div className="space-y-1.5">
                            <Label>Notas de resolución {resolveTargetStatus === 'resolved' ? '*' : ''}</Label>
                            <Textarea
                                value={resolveNotes}
                                onChange={e => setResolveNotes(e.target.value)}
                                placeholder="Describe la acción tomada, aeronave sustituta usada, tiempo de retraso final, etc."
                                rows={4}
                                data-testid="irops-resolve-notes"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setResolveDialog(null)}>Cancelar</Button>
                        <Button
                            onClick={submitResolve}
                            disabled={savingResolve || (resolveTargetStatus === 'resolved' && !resolveNotes.trim())}
                            className="btn-primary"
                            data-testid="irops-resolve-submit"
                        >
                            {savingResolve ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                            {resolveTargetStatus === 'closed' ? 'Cerrar' : 'Confirmar Resolución'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
